import { useEffect, useState } from "react";
import ApiResponse from "@core/types/apiResponse";
import { SearchableOption } from "@core/components/SearchableListPicker";
import {
    SORT_BY_CHURCH_NAME,
    SORT_BY_DISTRICT_NAME,
    SORT_DIR_ASC,
} from "@core/constants/listSort";
import { fetchDistrictsAsync } from "@features/district/services/districtSvc";
import { fetchChurchesAsync } from "@features/church/services/churchSvc";
import {
    ENTITY_SCOPE_SECTION_HEADERS,
    encodeEntityScopeDestination,
    formatChurchDistrictSublabel,
    formatEntityScopeLabel,
} from "@features/entity/constants/entityScopePicker";
import {
    getApiScopeFromValue,
    getInviteDestinationChildScopes,
    getScopeFromValue,
} from "@features/invitations/utils/getScopeFromParam";

interface PagedResponse {
    data?: any[];
    last_page?: number;
    meta?: { last_page?: number };
}

async function fetchAllPaged(
    fetchPage: (page: number) => Promise<ApiResponse<PagedResponse>>
): Promise<any[]> {
    const first = await fetchPage(1);
    if (!first.isSuccess()) throw new Error("Failed to load list");
    const firstData = first.getData();
    const items: any[] = [...(firstData?.data ?? [])];
    const lastPage = firstData?.last_page ?? firstData?.meta?.last_page ?? 1;

    if (lastPage > 1) {
        const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, i) => fetchPage(i + 2))
        );
        for (const r of rest) {
            if (r.isSuccess()) {
                const d = r.getData();
                if (d?.data) items.push(...d.data);
            }
        }
    }
    return items;
}

export interface EntityScopeOptionsResult {
    options: SearchableOption[];
    loading: boolean;
    error: boolean;
}

/**
 * Builds searchable entity scope options for the current workplace:
 * own entity plus districts and/or churches under them (nation includes both).
 */
export function useEntityScopeOptions(
    ownScopeValue: string,
    ownScopeRefId: number | undefined,
    ownLabel: string,
    enabled: boolean,
    activeOnly = false
): EntityScopeOptionsResult {
    const [options, setOptions] = useState<SearchableOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        const ownScope = getScopeFromValue(ownScopeValue);
        const ownApiScope = getApiScopeFromValue(ownScopeValue);

        if (!enabled || !ownScope || !(ownScopeRefId && ownScopeRefId > 0)) {
            setOptions([]);
            setError(false);
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(false);
            try {
                const list: SearchableOption[] = [
                    {
                        value: encodeEntityScopeDestination(ownScope, ownScopeRefId),
                        label: formatEntityScopeLabel(
                            ownScope,
                            ownLabel.replace(/^(Nation|District|Church):\s*/i, "").trim() || ownLabel
                        ),
                        group: ENTITY_SCOPE_SECTION_HEADERS[ownScope] ?? ownScope,
                    },
                ];

                if (/^(Nation|District|Church):/i.test(ownLabel.trim())) {
                    list[0].label = ownLabel.trim();
                }

                const childScopes = getInviteDestinationChildScopes(ownScope);

                if (ownApiScope === "nation" && childScopes.includes("DISTRICT")) {
                    const districts = await fetchAllPaged((page) =>
                        fetchDistrictsAsync({
                            all: true,
                            page,
                            sortBy: SORT_BY_DISTRICT_NAME,
                            sortDir: SORT_DIR_ASC,
                            ...(activeOnly && { isActive: true }),
                        })
                    );
                    const scopedDistricts = districts.filter(
                        (d) => d.nation_id == null || Number(d.nation_id) === Number(ownScopeRefId)
                    );
                    const districtById = new Map<number, string>();
                    for (const d of scopedDistricts) {
                        const id = Number(d.district_id);
                        const name = String(d.district_name ?? "");
                        districtById.set(id, name);
                        list.push({
                            value: encodeEntityScopeDestination("DISTRICT", id),
                            label: formatEntityScopeLabel("DISTRICT", name),
                            group: ENTITY_SCOPE_SECTION_HEADERS.DISTRICT,
                        });
                    }

                    if (childScopes.includes("CHURCH")) {
                        const churches = await fetchAllPaged((page) =>
                            fetchChurchesAsync({
                                all: true,
                                page,
                                sortBy: SORT_BY_CHURCH_NAME,
                                sortDir: SORT_DIR_ASC,
                                ...(activeOnly && { isActive: true }),
                            })
                        );
                        const districtIds = new Set(districtById.keys());
                        const nationChurches = churches.filter(
                            (c) => c.district_id != null && districtIds.has(Number(c.district_id))
                        );
                        for (const c of nationChurches) {
                            const churchId = Number(c.church_id);
                            const churchName = String(c.church_name ?? "");
                            const districtName =
                                (c.district_name != null && String(c.district_name).trim()) ||
                                districtById.get(Number(c.district_id)) ||
                                "";
                            list.push({
                                value: encodeEntityScopeDestination("CHURCH", churchId),
                                label: formatEntityScopeLabel("CHURCH", churchName),
                                sublabel: districtName
                                    ? formatChurchDistrictSublabel(districtName)
                                    : undefined,
                                group: ENTITY_SCOPE_SECTION_HEADERS.CHURCH,
                            });
                        }
                    }
                } else if (ownApiScope === "district" && childScopes.includes("CHURCH")) {
                    const churches = await fetchAllPaged((page) =>
                        fetchChurchesAsync({
                            all: true,
                            page,
                            sortBy: SORT_BY_CHURCH_NAME,
                            sortDir: SORT_DIR_ASC,
                            ...(activeOnly && { isActive: true }),
                        })
                    );
                    const scoped = churches.filter(
                        (c) => c.district_id == null || Number(c.district_id) === Number(ownScopeRefId)
                    );
                    const districtNameFallback =
                        ownLabel.replace(/^District:\s*/i, "").trim() || ownLabel;
                    for (const c of scoped) {
                        const churchId = Number(c.church_id);
                        const churchName = String(c.church_name ?? "");
                        const districtName =
                            (c.district_name != null && String(c.district_name).trim()) ||
                            districtNameFallback;
                        list.push({
                            value: encodeEntityScopeDestination("CHURCH", churchId),
                            label: formatEntityScopeLabel("CHURCH", churchName),
                            sublabel: districtName
                                ? formatChurchDistrictSublabel(districtName)
                                : undefined,
                            group: ENTITY_SCOPE_SECTION_HEADERS.CHURCH,
                        });
                    }
                }

                if (!cancelled) setOptions(list);
            } catch {
                if (!cancelled) {
                    setOptions([]);
                    setError(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [ownScopeValue, ownScopeRefId, ownLabel, enabled, activeOnly]);

    return { options, loading, error };
}
